import Input from '@ui/input';
import { bigNumberToReadable } from '@utils/big-number-to-readable';
import { filterPositiveNumber } from '@utils/filter-positive-number';
import { type FC, memo, useCallback, useState, useEffect } from 'react';
import styles from './input-section.module.scss';
import { isSuiCoinAddress } from '@utils/is-sui-coin-address';
import CoinLogo from '@ui/coin-logo';
import { scaleUp } from '@utils/math';
import { Decimal } from 'parrot-sui-sdk';
import { numericFormat } from '@utils/index';
import { type CoinStruct } from '@models/coins.vault.model';
import Block from '@components/block';

interface OwnProps {
  coin: CoinStruct;
  amount: string | undefined;
  onAmountChange(amount: string | undefined): void;
  disabled?: boolean;
  className?: string;
  balance: string;
  loading?: boolean;
}

const InputSection: FC<OwnProps> = (props) => {
  const { coin, onAmountChange, disabled, className, amount, balance, loading } = props;
  const [inputValue, setInputValue] = useState('');
  const [forcing, setFocusing] = useState(false);

  useEffect(() => {
    if (!forcing) {
      setInputValue(
        amount ? bigNumberToReadable(amount, coin.decimals, false, undefined, false) : '',
      );
    }
  }, [coin, amount]);

  const handleFocus = useCallback(() => {
    setFocusing(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusing(false);
  }, []);

  const handleValueChange = useCallback(
    (value: string) => {
      const { num, str } = filterPositiveNumber(value);
      setInputValue(str.trim());
      if (num > 0) {
        onAmountChange(scaleUp(new Decimal(num), coin.decimals).toFixed(0));
      } else {
        onAmountChange('');
      }
    },
    [coin.decimals, onAmountChange],
  );

  const handleSetMax = useCallback(() => {
    const maxBalance = isSuiCoinAddress(coin.coin_type)
      ? new Decimal(balance).minus(10 ** coin.decimals * 0.1).toNumber()
      : Number(balance);
    onAmountChange(maxBalance > 0 ? maxBalance.toFixed(0) : '');
  }, [onAmountChange, coin.coin_type, coin.decimals, balance]);

  const handleSetHalf = useCallback(() => {
    const maxBalance = Number(balance) * 0.5;
    onAmountChange(maxBalance > 0 ? maxBalance.toFixed(0) : '');
  }, [onAmountChange, balance]);

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <div className={styles.wrapper_con}>
        <div className={styles.right}>
          <Input
            wrapperClassName={styles.input}
            value={inputValue}
            onChangeText={handleValueChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder="0.00"
          />
        </div>

        <div className={styles.left}>
          <CoinLogo src={coin.icon} />
          <span>{coin.symbol}</span>
        </div>
      </div>

      <div className={styles.wrapper_con}>
        <div className={styles.coin_usd}>
          ${numericFormat(new Decimal(coin.price_usd).mul(!inputValue ? 0 : inputValue).toString())}
        </div>
        <div className={styles.coin}>
          Available:&nbsp;
          {loading ? (
            <Block width={60} />
          ) : (
            <span>{balance ? bigNumberToReadable(balance, coin.decimals) : 0}</span>
          )}
          <span className={styles.half} onClick={handleSetHalf}>
            Half
          </span>
          <span className={styles.max} onClick={handleSetMax}>
            Max
          </span>
        </div>
      </div>
    </div>
  );
};

export default memo(InputSection);
